/**
 * Acha links no texto do bate-papo.
 *
 * O texto continua sendo renderizado como TEXTO pelo React — nada de HTML vindo
 * de mensagem alheia. Aqui so decidimos quais pedacos viram `<a>`, e a etiqueta
 * do link e sempre o proprio texto que a pessoa escreveu: nao ha como escrever
 * "clique aqui" apontando pra outro lugar.
 */

export type ChatSegment =
    | { kind: 'text'; value: string }
    | { kind: 'link'; value: string; href: string }

/**
 * `http(s)://` explicito ou `www.` no comeco. Dominio solto (`exemplo.com`)
 * fica de fora de proposito: pegaria "arquivo.txt" e "versao 1.2" no meio de
 * uma frase.
 */
const URL_RE = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi

/**
 * Pontuacao colada no fim quase sempre e da frase, nao do endereco:
 * "veja https://exemplo.com." nao deve levar o ponto pro link.
 */
const TRAILING = /[.,;:!?'"]+$/

export function linkify(text: string): ChatSegment[] {
    const segments: ChatSegment[] = []
    let cursor = 0

    for (const match of text.matchAll(URL_RE)) {
        const inicio = match.index ?? 0
        const bruto = match[0]
        const { url, sobra } = trimTrailing(bruto)
        const href = safeHref(url)

        if (inicio > cursor) segments.push({ kind: 'text', value: text.slice(cursor, inicio) })

        if (href) segments.push({ kind: 'link', value: url, href })
        // Endereco que nao passa na checagem volta a ser texto comum: melhor
        // aparecer sem virar link do que sumir da mensagem.
        else segments.push({ kind: 'text', value: url })

        if (sobra) segments.push({ kind: 'text', value: sobra })
        cursor = inicio + bruto.length
    }

    if (cursor < text.length) segments.push({ kind: 'text', value: text.slice(cursor) })
    return segments
}

/** Separa a pontuacao final que pertence a frase, respeitando parenteses. */
function trimTrailing(raw: string): { url: string; sobra: string } {
    let url = raw

    // ")" so sai quando nao ha "(" pra fechar — links de wiki costumam
    // terminar em parenteses de verdade.
    while (url.endsWith(')') && contar(url, ')') > contar(url, '(')) {
        url = url.slice(0, -1)
    }
    url = url.replace(TRAILING, '')

    return { url, sobra: raw.slice(url.length) }
}

function contar(texto: string, char: string): number {
    let total = 0
    for (const atual of texto) if (atual === char) total += 1
    return total
}

/**
 * `null` pra tudo que nao for um endereco web comum.
 *
 * Duas recusas importam:
 *
 * - **protocolo**: `javascript:` num href e execucao de codigo com um clique, e
 *   `data:` serve pra servir uma pagina inteira de dentro da mensagem. So
 *   `http` e `https` passam.
 * - **credencial na URL**: `https://banco.com@site-falso.com` parece o banco e
 *   leva pro outro lugar. Sem virar link, o endereco fica visivel como texto e
 *   ninguem clica por engano.
 */
export function safeHref(raw: string): string | null {
    const candidato = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`

    try {
        const url = new URL(candidato)
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
        if (url.username || url.password) return null
        if (!url.hostname) return null
        return url.toString()
    } catch {
        return null
    }
}
